import { useState, useMemo, Fragment } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Grow,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { CancelFormActionButton, PrimaryFormActionButton } from "../../../Features/Table/GenericDraggableTable/TableStyles";
import { deleteOrgLocationMaster } from "../../../Api/RbacAdminApi";
import CODE from "../../../Static/Constants/StatusCodes";
import { useToast } from "../../../Hooks/useToast";

const DeleteLocationDialog = ({ target, locationLevels, onClose, onSuccess }) => {
  const [deleting, setDeleting] = useState(false);
  const { openNotification } = useToast();

  const context = useMemo(() => {
    if (!target || !locationLevels.length) return { parts: [], lastType: "Location" };
    const parts = locationLevels.map((lvl) => target[lvl.level]).filter(Boolean);
    const filledLevels = locationLevels.filter((lvl) => target[lvl.level]);
    const lastType = filledLevels[filledLevels.length - 1]?.type || "Location";
    return { parts, lastType };
  }, [target, locationLevels]);

  const handleConfirm = async () => {
    setDeleting(true);
    try {
      const res = await deleteOrgLocationMaster(target.id);
      if (res?.responseStatus?.code === CODE.SUCCESS) {
        openNotification({ text: "Location deleted successfully", icon: "toast-success" });
        onSuccess();
      } else {
        openNotification({
          text: res?.responseStatus?.msg || "Failed to delete location",
          icon: "toast-error",
        });
      }
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog
      open={!!target}
      onClose={() => !deleting && onClose()}
      maxWidth={false}
      TransitionComponent={Grow}
      TransitionProps={{ timeout: 180 }}
      onKeyDown={(e) => { if (e.key === "Enter" && !deleting && target) handleConfirm(); }}
      slotProps={{ backdrop: { sx: { backdropFilter: "blur(4px)" } } }}
      PaperProps={{
        sx: {
          maxWidth: 540,
          width: "100%",
          borderRadius: "12px",
          borderTop: "3px solid rgba(239,68,68,0.8)",
          boxShadow: "0 16px 40px rgba(0,0,0,0.14)",
        },
      }}
    >
      <DialogContent sx={{ pt: 4, pb: 2, px: 3.5 }}>
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: "#FFF0F0",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <DeleteIcon sx={{ fontSize: 30, color: "#EF4444" }} />
          </Box>
        </Box>

        <Typography
          sx={{ fontSize: 16, fontWeight: 700, color: "#1A2232", mb: 3, textAlign: "center" }}
        >
          Delete Location
        </Typography>

        <Typography sx={{ fontSize: 14, color: "#3D4B5A", lineHeight: "22px", mb: 1.5 }}>
          Are you sure you want to delete:
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "4px",
            mb: 2,
            px: 1.5,
            py: 1,
            backgroundColor: "#F8FAFC",
            borderRadius: "8px",
            border: "1px solid #E2E8F0",
          }}
        >
          {context.parts.map((part, i) => (
            <Fragment key={i}>
              {i > 0 && (
                <Box
                  component="span"
                  sx={{ color: "#CBD5E1", fontSize: 13, userSelect: "none", mx: "2px" }}
                >
                  /
                </Box>
              )}
              <Box
                component="span"
                sx={{
                  fontSize: 13,
                  fontWeight: i === context.parts.length - 1 ? 600 : 400,
                  color: i === context.parts.length - 1 ? "#1A2232" : "#94A3B8",
                }}
              >
                {part}
              </Box>
            </Fragment>
          ))}
        </Box>

        <Typography sx={{ fontSize: 13, color: "#6B7280", lineHeight: 1.5 }}>
          This action will permanently remove this location and associated mappings. This cannot be undone.
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3.5,
          pb: 3,
          pt: 0,
          mt: 3,
          gap: 1.5,
          "@media (max-width: 480px)": {
            flexDirection: "column",
            "& > button": { width: "100%", minWidth: "unset" },
          },
        }}
      >
        <CancelFormActionButton
          onClick={onClose}
          disabled={deleting}
          sx={{
            minWidth: 110,
            background: "#F8FAFC",
            border: "1px solid #E2E8F0",
            "&:hover": { background: "#F1F5F9" },
          }}
        >
          Cancel
        </CancelFormActionButton>
        <PrimaryFormActionButton
          autoFocus
          onClick={handleConfirm}
          disabled={deleting}
          sx={{
            minWidth: 150,
            background: "#EF4444",
            boxShadow: "none",
            "&:hover": { background: "#DC2626", boxShadow: "0 2px 8px rgba(239,68,68,0.35)" },
            "&.Mui-disabled": { background: "#CCCCCC", color: "#9F9F9F", boxShadow: "none" },
          }}
        >
          {deleting ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={15} sx={{ color: "#fff" }} />
              Deleting...
            </Box>
          ) : (
            "Delete Location"
          )}
        </PrimaryFormActionButton>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteLocationDialog;
